// Add to Cart request logic for Product Detail Page.
//  See "productListing/ProductBox/AddToCart/request.ts" for Product Listing Page.
import { ThrowableStatus } from "@wss/error-tracking/throwable";
import {
  addToCartAnalytics,
  AtcAnalyticsProps,
} from "~/analytics/AddToCart/addToCartAnalytics";
import {
  AddToCartErrorMessages,
  AddToCartErrorNames,
} from "~/common/AddToCart/Constants";
import Store from "~/common/Store";
import { incrementItemsInCart } from "~/common/Store/actions/user";

export interface AddToCartProduct {
  itemNumber: string;
  price: number;
  minimumBuy: number;
  mustBuy: number;
  feedIdentifier: string;
  outletId?: number;
  originalId?: string;
  isVirtualPart?: boolean;
  description?: string;
  link?: string;
}

const tryParseError = async (
  response: Response,
): Promise<ViewCartErrorResponse | null> => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

type ViewCartSuccessResponse = Record<string, string>;
type ViewCartErrorResponse = { errorMessage: string; errorName: string };
type ViewCartResponse = ViewCartSuccessResponse | ViewCartErrorResponse;

const getResponse = async (
  body: FormData,
): Promise<ThrowableStatus<ViewCartResponse>> => {
  try {
    const response = await fetch("/viewcart.cfm", {
      body,
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      const errorJson = await tryParseError(response);
      return {
        isError: true,
        status: response.status,
        error: new Error(errorJson?.errorName ?? response.statusText),
      };
    }

    const data: ViewCartSuccessResponse = await response.json();
    return { isError: false, value: data };
  } catch (error) {
    return {
      isError: true,
      status: null,
      error: new Error("There was an error with the 'viewcart.cfm' endpoint", {
        cause: error,
      }),
    };
  }
};

const buildBaseRequestBody = (
  requestItemNumber: string,
  product: AddToCartProduct,
  quantity: number,
) => {
  const {
    itemNumber,
    isVirtualPart,
    outletId,
    price,
    minimumBuy,
    mustBuy,
    feedIdentifier,
    description,
    link,
  } = product;

  const body = new FormData();

  body.set("from", "add");
  body.set("price", price.toString());
  body.set("mnbuy", minimumBuy.toString());
  body.set("mxbuy", mustBuy.toString());
  body.set("feed_identifier", feedIdentifier);
  body.set("item_number", requestItemNumber);
  body.set("qty", quantity.toString());
  if (outletId) {
    body.set(
      "description",
      `Outlet Item #${outletId} (${itemNumber.toUpperCase()})`,
    );
  }

  if (isVirtualPart) {
    if (description) {
      body.set("description", description);
    }

    if (link) {
      body.set("link", link);
    }
  }

  return body;
};

interface Params {
  product: AddToCartProduct;
  quantity: number;
  autoReorderInterval?: string;
  serviceTierPricingIds?: number[];
  accessoryIds?: number[];
  warrantyPlanId?: number;
  isSample?: boolean;
  openCartLimitModal?: (cartLimitMessage: string) => void;
  gtmAtcProps: AtcAnalyticsProps;
}

const request = async ({
  product,
  quantity,
  autoReorderInterval = "0",
  serviceTierPricingIds = [],
  accessoryIds = [],
  warrantyPlanId = 0,
  isSample = false,
  openCartLimitModal,
  gtmAtcProps,
}: Params): Promise<boolean> => {
  const { itemNumber, originalId, isVirtualPart, outletId } = product;

  let requestItemNumber = itemNumber;
  if (outletId) {
    requestItemNumber = "OUTLET";
  } else if (isVirtualPart && originalId) {
    requestItemNumber = originalId;
  }

  try {
    const body = buildBaseRequestBody(requestItemNumber, product, quantity);
    if (accessoryIds.length > 0) {
      body.set("accessories", accessoryIds.join());
    }

    if (serviceTierPricingIds.length > 0) {
      body.set("serviceTierPricingIds", serviceTierPricingIds.join());
    }

    if (warrantyPlanId > 0) {
      body.set("productWarrantyId", warrantyPlanId.toString());
    }

    body.set("auto_reorder_interval_id", autoReorderInterval);
    body.set("dynamicadd", "1");
    body.set("isSample", isSample.toString());

    const response = await getResponse(body);
    if (response.isError) {
      if (response.error.message === AddToCartErrorNames.cartItemCountLimit) {
        openCartLimitModal?.(
          AddToCartErrorMessages.cartItemCountLimitMessage ?? "",
        );
      }
      return false;
    }

    Store.dispatch(incrementItemsInCart(quantity));
  } catch (error) {
    return false;
  }

  addToCartAnalytics(gtmAtcProps, { itemNumber: requestItemNumber, quantity });
  return true;
};

export default request;
